import { create } from 'zustand';
import { getMeChecklists } from '../api/generated/me-checklists/me-checklists';
import { getDocuments } from '../api/generated/documents/documents';
import type {
  FacilityFlowCoreModulesPlanningDtosChecklistsMyDayChecklistsDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsMyOpenChecklistsDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsTaskChecklistGroupDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistItemStateDto,
} from '../api/generated/models';

const {
  getApiV1MeChecklists,
  getApiV1MeChecklistsOpen,
  postApiV1MeChecklistsComplete,
  deleteApiV1MeChecklistsCompletionsCompletionId,
} = getMeChecklists();

const { getApiV1DocumentsIdContent } = getDocuments();

export type MyDayChecklists = FacilityFlowCoreModulesPlanningDtosChecklistsMyDayChecklistsDto;
export type MyOpenChecklists = FacilityFlowCoreModulesPlanningDtosChecklistsMyOpenChecklistsDto;
export type TaskChecklistGroup = FacilityFlowCoreModulesPlanningDtosChecklistsTaskChecklistGroupDto;
export type ChecklistItemState = FacilityFlowCoreModulesPlanningDtosChecklistsChecklistItemStateDto;

/** One past day and the tasks of that day that still have unconfirmed items. */
export interface OpenChecklistDayGroup {
  date: string;
  tasks: TaskChecklistGroup[];
}

/** Mirrors the column length configured on the backend completion entity. */
export const CHECKLIST_NOTE_MAX = 500;

interface CompleteArgs {
  date: string;
  taskId: string;
  checklistItemId: string;
  /** Optional remark stored with the confirmation (defect found, room locked, …). */
  note?: string;
  photo?: File;
}

interface MyChecklistState {
  /** Day state keyed by ISO date — dashboard and day plan may look at different days. */
  days: Record<string, MyDayChecklists>;
  loading: boolean;
  /** Id of the item currently being confirmed/revoked, so only that row shows a spinner. */
  pendingItemId: string | null;
  error: string | null;

  /** Everything still unconfirmed from past days; null until it has been loaded once. */
  openPast: MyOpenChecklists | null;
  openPastLoading: boolean;
  openPastError: string | null;
  /** Whether the loaded `openPast` also carries fully confirmed tasks (bounded by the server). */
  openPastIncludesCompleted: boolean;

  fetchDay: (date: string) => Promise<void>;
  /**
   * Loads the past days. Callers that only warn about open work (dashboard, clock-out guard)
   * keep the default; the checklist page passes `true` for its "show completed" switch.
   */
  fetchOpenPast: (includeCompleted?: boolean) => Promise<void>;
  completeItem: (args: CompleteArgs) => Promise<void>;
  uncompleteItem: (date: string, completionId: string) => Promise<void>;
  openPhoto: (documentId: string) => Promise<void>;
  clear: () => void;
}

function countOpen(tasks: TaskChecklistGroup[]): number {
  return tasks.reduce((sum, task) => sum + (task.items ?? []).filter((i) => !i.isCompleted).length, 0);
}

/** Open (unconfirmed) items of a day; 0 when the day has not been loaded. */
export function openCountFor(state: MyChecklistState, date: string): number {
  return countOpen(state.days[date]?.tasks ?? []);
}

/**
 * The blocking task that still has open items and therefore holds back `taskId` (FACF-113).
 * Mirrors the server rule: a blocking task never blocks itself.
 */
export function openBlockerFor(
  state: MyChecklistState,
  date: string,
  taskId: string | undefined,
): TaskChecklistGroup | null {
  const tasks = state.days[date]?.tasks ?? [];
  return (
    tasks.find(
      (task) =>
        task.isBlocking &&
        task.taskId !== taskId &&
        (task.items ?? []).some((i) => !i.isCompleted),
    ) ?? null
  );
}

/** Tasks of a day that still have unconfirmed items — drives the clock-out warning. */
export function openTasksFor(state: MyChecklistState, date: string): TaskChecklistGroup[] {
  const day = state.days[date];
  if (!day?.tasks) return [];
  return day.tasks.filter((task) => (task.items ?? []).some((i) => !i.isCompleted));
}

/**
 * Past days with unconfirmed items, newest first — derived straight from the payload so a
 * component can memoise on the stored object itself.
 *
 * Days on or after `todayIso` are dropped: the server decides what counts as past, and around
 * midnight its date and the browser's can disagree, which would render the same day twice.
 *
 * With `includeCompleted` fully confirmed tasks stay in. Everything that warns about open work
 * (dashboard, clock-out guard, counters) keeps the default, so a payload that was loaded with
 * completed tasks never inflates them.
 */
export function toOpenDayGroups(
  openPast: MyOpenChecklists | null,
  todayIso: string,
  includeCompleted = false,
): OpenChecklistDayGroup[] {
  return (openPast?.days ?? [])
    .filter((day): day is MyDayChecklists & { date: string } => !!day.date && day.date < todayIso)
    .map((day) => ({
      date: day.date,
      tasks: includeCompleted
        ? (day.tasks ?? [])
        : (day.tasks ?? []).filter((task) => (task.items ?? []).some((i) => !i.isCompleted)),
    }))
    .filter((group) => group.tasks.length > 0)
    .sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Same thing off the whole store.
 *
 * why: returns a fresh array on every call, so never use it as a hook selector — subscribe to
 * `openPast` and call {@link toOpenDayGroups} inside a useMemo, otherwise useSyncExternalStore
 * loops.
 */
export function openPastDayGroups(
  state: MyChecklistState,
  todayIso: string,
): OpenChecklistDayGroup[] {
  return toOpenDayGroups(state.openPast, todayIso);
}

/** Open items of past days — counted from the deduped groups, not from the raw payload. */
export function openPastCount(state: MyChecklistState, todayIso: string): number {
  return toOpenDayGroups(state.openPast, todayIso).reduce(
    (sum, group) => sum + countOpen(group.tasks),
    0,
  );
}

/** What the employee still owes in total: today plus every past day. */
export function totalOpenCount(state: MyChecklistState, todayIso: string): number {
  return openCountFor(state, todayIso) + openPastCount(state, todayIso);
}

export const useMyChecklistStore = create<MyChecklistState>((set, get) => ({
  days: {},
  loading: false,
  pendingItemId: null,
  error: null,
  openPast: null,
  openPastLoading: false,
  openPastError: null,
  openPastIncludesCompleted: false,

  fetchDay: async (date) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1MeChecklists({ date });
      set((s) => ({ days: { ...s.days, [date]: data }, loading: false }));
    } catch {
      set({ error: 'dashboard:checklist.errors.fetchFailed', loading: false });
    }
  },

  fetchOpenPast: async (includeCompleted = false) => {
    set({ openPastLoading: true, openPastError: null });
    try {
      const data = await getApiV1MeChecklistsOpen(includeCompleted ? { includeCompleted } : undefined);
      set({ openPast: data, openPastLoading: false, openPastIncludesCompleted: includeCompleted });
    } catch {
      set({ openPastError: 'dashboard:checklist.errors.fetchFailed', openPastLoading: false });
    }
  },

  completeItem: async ({ date, taskId, checklistItemId, note, photo }) => {
    set({ pendingItemId: checklistItemId, error: null });
    try {
      await postApiV1MeChecklistsComplete({ date, taskId, checklistItemId, note, photo });
      // why: refetch instead of patching locally — the server owns completionId, timestamp
      // and photo id, and the badge counts must not drift from what the protocol will show.
      await refreshAfterMutation(get, date);
    } finally {
      set({ pendingItemId: null });
    }
  },

  uncompleteItem: async (date, completionId) => {
    set({ pendingItemId: completionId, error: null });
    try {
      await deleteApiV1MeChecklistsCompletionsCompletionId(completionId);
      await refreshAfterMutation(get, date);
    } finally {
      set({ pendingItemId: null });
    }
  },

  // why: the blob endpoint needs the auth cookie *and* the X-Tenant-Id header that the
  // axios instance injects, so a plain <a href> would fail tenant validation.
  openPhoto: async (documentId) => {
    const blob = await getApiV1DocumentsIdContent(documentId);
    const url = URL.createObjectURL(blob as Blob);
    window.open(url, '_blank', 'noopener');
    // The tab keeps its own reference; release ours once it has had a chance to load.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  clear: () =>
    set({
      days: {},
      error: null,
      pendingItemId: null,
      openPast: null,
      openPastError: null,
      openPastIncludesCompleted: false,
    }),
}));

/**
 * Re-reads what a confirmation on `date` can have changed: that day when a view holds it, and
 * the open-past list once anything has loaded it — ticking the last item off a past task
 * removes that task from the list, so a stale copy would keep warning about finished work.
 */
async function refreshAfterMutation(get: () => MyChecklistState, date: string): Promise<void> {
  const state = get();
  const jobs: Promise<void>[] = [];

  if (state.days[date] !== undefined) jobs.push(state.fetchDay(date));
  // why: keep the mode the list was loaded in, otherwise ticking an item would flip the page's
  // "show completed" view back to open-only.
  if (state.openPast !== null) jobs.push(state.fetchOpenPast(state.openPastIncludesCompleted));
  if (jobs.length === 0) jobs.push(state.fetchDay(date));

  await Promise.all(jobs);
}
